import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  UserPlus, Loader2, Trash2, Save, Mail, ShieldCheck, Clock, RefreshCw, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { key: "full", label: "Full access", hint: "All admin sections" },
  { key: "listings", label: "Listings" },
  { key: "kyc", label: "Host KYC" },
  { key: "bookings", label: "Bookings" },
  { key: "guests", label: "Guests" },
  { key: "hosts", label: "Hosts", hint: "Host commissions" },
  { key: "ads", label: "Ads" },
  { key: "messages", label: "Messages" },
  { key: "twilio", label: "SMS" },
  { key: "payments", label: "Payments" },
  { key: "payouts", label: "Payouts", hint: "Release host payouts" },
  { key: "terms", label: "Terms" },
];

function RoleChips({ roles }) {
  if (!roles?.length) return <span className="text-xs text-slate-400">No roles</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {roles.map((r) => (
        <span
          key={r}
          className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            r === "full" ? "bg-forest text-white" : "bg-slate-100 text-slate-700"
          }`}
        >
          {r}
        </span>
      ))}
    </div>
  );
}

function RoleCheckboxes({ value, onChange, idPrefix }) {
  const isFull = value.includes("full");
  const toggle = (k) => {
    if (k === "full") return onChange(isFull ? [] : ["full"]);
    if (isFull) return; // full covers everything
    onChange(value.includes(k) ? value.filter((x) => x !== k) : [...value, k]);
  };
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {ROLE_OPTIONS.map((r) => {
        const active = r.key === "full" ? isFull : (isFull || value.includes(r.key));
        return (
          <label
            key={r.key}
            data-testid={`${idPrefix}-role-${r.key}`}
            className={`flex cursor-pointer items-start gap-2 rounded-xl border p-2.5 text-sm transition ${
              active ? "border-copper bg-copper/10" : "border-slate-200 bg-white hover:bg-slate-50"
            }`}
          >
            <input
              type="checkbox"
              checked={active}
              onChange={() => toggle(r.key)}
              disabled={r.key !== "full" && isFull}
              className="mt-0.5 h-4 w-4"
            />
            <div className="min-w-0">
              <div className="font-semibold text-slate-800">{r.label}</div>
              {r.hint && <div className="text-[11px] text-slate-500">{r.hint}</div>}
            </div>
          </label>
        );
      })}
    </div>
  );
}

export default function AdminTeam() {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRoles, setInviteRoles] = useState(["listings"]);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRoles, setEditRoles] = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/team`);
      setAdmins(data.admins || []);
      setInvites(data.pending_invites || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const sendInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return toast.error("Email is required");
    if (!inviteRoles.length) return toast.error("Pick at least one role");
    try {
      setSending(true);
      const { data } = await axios.post(`${API}/admin/team/invite`, {
        email: inviteEmail.trim().toLowerCase(),
        name: inviteName.trim(),
        roles: inviteRoles,
      });
      toast.success(
        data.status === "promoted"
          ? `Existing user ${data.email} was granted admin access`
          : `Invite sent to ${data.email}`
      );
      setInviteEmail("");
      setInviteName("");
      setInviteRoles(["listings"]);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const startEdit = (a) => {
    setEditingId(a.user_id);
    setEditRoles([...(a.admin_roles || [])]);
  };
  const cancelEdit = () => { setEditingId(null); setEditRoles([]); };
  const saveEdit = async (a) => {
    try {
      setSavingEdit(true);
      await axios.patch(`${API}/admin/team/${a.user_id}`, { roles: editRoles });
      toast.success(`Roles updated for ${a.email}`);
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const revokeAdmin = async (a) => {
    if (!window.confirm(`Revoke admin access for ${a.email}? They keep their account, just lose the admin panel.`)) return;
    try {
      await axios.delete(`${API}/admin/team/${a.user_id}`);
      toast.success(`Admin access revoked for ${a.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Revoke failed");
    }
  };

  const cancelInvite = async (inv) => {
    if (!window.confirm(`Cancel the pending invite to ${inv.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/team/invites/${inv.id}`);
      toast.success(`Invite cancelled`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const resendInvite = async (inv) => {
    try {
      await axios.post(`${API}/admin/team/invites/${inv.id}/resend`);
      toast.success(`Invite re-sent to ${inv.email}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="admin-team">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-widest text-slate-500">Team</div>
        <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Admin users</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Invite people to help run EasyStay. Each admin can be scoped to just the sections they need — or given full access.
        </p>
      </div>

      {/* Invite form */}
      <form
        onSubmit={sendInvite}
        className="mb-8 rounded-2xl border border-slate-200 bg-white p-5 card-shadow"
        data-testid="invite-form"
      >
        <div className="mb-3 flex items-center gap-2">
          <UserPlus className="h-4 w-4 text-copper" />
          <h2 className="font-display text-lg font-bold text-slate-900">Invite a new admin</h2>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Email</label>
            <input
              data-testid="invite-email-input"
              type="email"
              required
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="new.admin@example.com"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Name (optional)</label>
            <input
              data-testid="invite-name-input"
              type="text"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Jane Doe"
              className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-copper focus:bg-white"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="text-xs uppercase tracking-widest text-slate-500">Roles</label>
          <div className="mt-2">
            <RoleCheckboxes value={inviteRoles} onChange={setInviteRoles} idPrefix="invite" />
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end">
          <button
            type="submit"
            data-testid="invite-submit-btn"
            disabled={sending}
            className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {sending ? "Sending…" : "Send invite"}
          </button>
        </div>
      </form>

      {/* Current admins */}
      <div className="mb-8">
        <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <ShieldCheck className="h-4 w-4 text-forest" />
          Current admins ({admins.length})
        </h2>
        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500 card-shadow">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white card-shadow">
            <table className="w-full text-sm" data-testid="admins-table">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">User</th>
                  <th className="px-4 py-3">Roles</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {admins.map((a) => (
                  <tr key={a.user_id} data-testid={`admin-row-${a.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{a.name || "—"}</div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                      {a.is_super_admin && (
                        <span className="mt-1 inline-block rounded-full bg-forest px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                          Super admin
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === a.user_id ? (
                        <RoleCheckboxes value={editRoles} onChange={setEditRoles} idPrefix={`edit-${a.email}`} />
                      ) : (
                        <RoleChips roles={a.admin_roles} />
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {a.is_super_admin ? (
                        <span className="text-xs text-slate-400">Protected</span>
                      ) : editingId === a.user_id ? (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={cancelEdit}
                            data-testid={`cancel-edit-${a.email}`}
                            className="text-xs text-slate-500 hover:text-slate-800"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => saveEdit(a)}
                            disabled={savingEdit}
                            data-testid={`save-edit-${a.email}`}
                            className="btn-primary flex items-center gap-1 text-xs !py-2 !px-3 disabled:opacity-50"
                          >
                            {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                            Save
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => startEdit(a)}
                            data-testid={`edit-${a.email}`}
                            className="text-xs text-slate-600 hover:text-copper"
                          >
                            Edit roles
                          </button>
                          <button
                            onClick={() => revokeAdmin(a)}
                            data-testid={`revoke-${a.email}`}
                            className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
                          >
                            <Trash2 className="h-3 w-3" /> Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {!admins.length && (
                  <tr><td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-400">No admins yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div>
          <h2 className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-900">
            <Clock className="h-4 w-4 text-copper" />
            Pending invites ({invites.length})
          </h2>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white card-shadow">
            <table className="w-full text-sm" data-testid="pending-invites-table">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Roles</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {invites.map((inv) => (
                  <tr key={inv.id} data-testid={`invite-row-${inv.email}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{inv.email}</div>
                      {inv.name && <div className="text-xs text-slate-500">{inv.name}</div>}
                    </td>
                    <td className="px-4 py-3"><RoleChips roles={inv.roles} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {inv.created_at ? new Date(inv.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => resendInvite(inv)}
                          data-testid={`resend-invite-${inv.email}`}
                          className="flex items-center gap-1 text-xs text-slate-600 hover:text-copper"
                        >
                          <RefreshCw className="h-3 w-3" /> Resend
                        </button>
                        <button
                          onClick={() => cancelInvite(inv)}
                          data-testid={`cancel-invite-${inv.email}`}
                          className="flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
                        >
                          <X className="h-3 w-3" /> Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
